import moment from "moment";
import {
  Box,
  Button,
  Container,
  Flex,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import { IconDownload, IconPrinter } from "@tabler/icons-react";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { CSVLink } from "react-csv";
import { useQuery } from "react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";

import AppBreadcrumb from "components/AppBreadcrumb";
import FloatingMenu from "components/FloatingMenu";
import { PrintModal } from "components/PrintModal";
import { report_api } from "./report.service";
import { showErrorToast } from "utilities/Toast";

export default function SalesReport() {
  const isMobileScreen = useMediaQuery("(max-width: 768px)");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchClicked, setSearchClicked] = useState(false);

  const [columnFilters, setColumnFilters] = useState([]);

  const [csv, setCsv] = useState([]);
  const [isCsv, setIsCsv] = useState(false);
  const [filename, setFilename] = useState("sales_report.csv");

  const [head, setHead] = useState([]);
  const [body, setBody] = useState([]);
  const [foot, setFoot] = useState([]);
  const [view, setView] = useState("collection"); 
// "collection" | "credit"


  const componentRef = useRef();
  const csvRef = useRef();

  const [isPrint, setIsPrint] = useState(false);

useEffect(() => {
  if (isPrint && body.length) {
    setTimeout(() => {
      handlePrint();
      setIsPrint(false);
    }, 100); // 🔥 allow DOM to render PrintModal
  }
}, [isPrint, body]);



  /* ---------------- FORMATTER ---------------- */
  const formatAmount = value =>
    value === null || value === undefined || isNaN(value)
      ? "-"
      : `₹ ${parseFloat(value).toFixed(2)}`;

  /* ---------------- COLUMNS ---------------- */

  const collectionColumns = useMemo(
    () => [
      {
        header: "Counter Name",
        accessorKey: "counter_name",
        size:"auto",
        filterFn: "includesString",
      },
      {
        header: "User",
        accessorKey: "username",
        size:"auto",
        filterFn: "includesString",
      },
      {
        header: "Cash",
        accessorKey: "cash",
        size:"auto",
        Cell: ({ cell }) => formatAmount(cell.getValue()),
      },
      {
        header: "Card",
        accessorKey: "card",
        size:"auto",
        Cell: ({ cell }) => formatAmount(cell.getValue()),
      },
      {
        header: "UPI",
        accessorKey: "upi",
        size:"auto",
        Cell: ({ cell }) => formatAmount(cell.getValue()),
      },
      {
        header: "Gift",
        accessorKey: "gift",
        size:"auto",
        Cell: ({ cell }) => formatAmount(cell.getValue()),
      },
      // {
      //   header: "Credit",
      //   accessorKey: "credit",
      //   size:"auto",
      //   Cell: ({ cell }) => formatAmount(cell.getValue()),
      // },
      {
  header: "Date",

  // ✅ return real Date object for sorting
  accessorFn: row => new Date(row.date),

  // needed for filtering
  id: "date",

  // ✅ tell Mantine to sort as date
  sortingFn: "datetime",

  // keep date-range filter
  filterVariant: "date-range",

  // UI display format
  Cell: ({ cell }) =>
    moment(cell.getValue()).format("DD/MM/YYYY"),
},

      {
        header: "Total",
        accessorKey: "total_amount",
        Cell: ({ cell }) => formatAmount(cell.getValue()),
      },
    ],
    []
  );

const creditColumns = useMemo(() => [
  {
    header: "Counter Name",
    accessorKey: "counter_name",
  },
  {
    header: "User",
    accessorKey: "username",
  },
  {
    header: "Customer",
    accessorKey: "customer",
  },
  {
    header: "Credit Amount",
    accessorKey: "credit",
    Cell: ({ cell }) => formatAmount(cell.getValue()),
  },
  {
    header: "Date",
     id: "date",
     sortingFn: "datetime",
    filterVariant: "date-range",
    accessorFn: row => new Date(row.date),
    Cell: ({ cell }) =>
      moment(cell.getValue()).format("DD/MM/YYYY"),
  },
], []);


const columns = useMemo(
  () => (view === "credit" ? creditColumns : collectionColumns),
  [view, creditColumns, collectionColumns]
);
  /* ---------------- API ---------------- */
const data = useQuery(
  ["sales-report", startDate, endDate, view],
  () => {
    const from = moment(startDate).format("YYYY-MM-DD");
    const to = moment(endDate || startDate).format("YYYY-MM-DD");

    return report_api.get_sales_report(
      from,
      to,
      view === "credit" ? "credit" : null
    );
  },
  { enabled: searchClicked }
);


  /* ---------------- TABLE INSTANCE (🔥 IMPORTANT) ---------------- */
  const table = useMantineReactTable({
    columns,
    data: (data?.data?.data || []).sort((a, b) =>
      a?.counter_name?.localeCompare(b?.counter_name)
    ),
    enableStickyHeader: true,
    enableFilterMatchHighlighting: true,
    enableSorting: true,
    enablePagination: true,
    manualFiltering: false,
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    renderTopToolbarCustomActions: () => (
  <Flex px={24} py={10} justify="space-between" align="flex-start">
    {/* LEFT */}
    <Text
      fw={600}
      tt="uppercase"
      size="sm"
      sx={theme => ({
        letterSpacing: 1,
        color: theme.colors.brand[7],
      })}
    >
      Sales Report List
    </Text>

    {/* RIGHT */}
    {view === "credit" && (
  <Box mt={-4} mr={0} ml={704}>
    {/*        👆 remove mr, add ml */}
    <Text
      px={10}
      py={4}
      fw={600}
      size="xs"
      sx={theme => ({
        borderRadius: 20,
        backgroundColor: theme.colors.red[0],
        color: theme.colors.red[7],
      })}
    >
      TOTAL CREDIT ({formatAmount(creditTotal)})
    </Text>
  </Box>
)}

  </Flex>
)

  });

  // 🔹 Credit Total
  const creditTotal = useMemo(() => {
    if (view !== "credit") return 0;

    const rows = table.getFilteredRowModel().rows;

    return rows.reduce((sum, row) => {
      return sum + Number(row.original.credit || 0);
    }, 0);
  }, [view, table.getFilteredRowModel().rows]);


  /* ---------------- PRINT ---------------- */
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  /* ---------------- CSV + PRINT LOGIC ---------------- */
const printFilter = (forCsv = true) => {
  const filteredRows = table.getFilteredRowModel().rows;

  const tableData =
    filteredRows.length > 0
      ? filteredRows.map(r => r.original)
      : data?.data?.data || [];

  if (!tableData.length) {
    showErrorToast({
      title: "Error",
      message: "No data available",
    });
    return;
  }

  const config =
    view === "credit" ? buildCreditExport(tableData) : buildCollectionExport(tableData);

  if (forCsv) {
    setFilename(config.filename);
    setCsv(config.csvData);
    setIsCsv(true);
  } else {
    setHead(config.headers);
    setBody(config.body);
    setIsPrint(true);
  }
};

//Collection data
const buildCollectionExport = tableData => {
  const headers = [
    "Counter Name",
    "User",
    "Cash",
    "Card",
    "UPI",
    "Gift",
    "Date",
    "Total",
  ];

  const dateMap = {};
  let grandTotal = 0;

  tableData.forEach(row => {
    const dateKey = moment(row.date).format("DD/MM/YYYY");

    const rowTotal =
      Number(row.cash || 0) +
      Number(row.card || 0) +
      Number(row.upi || 0) +
      Number(row.gift || 0);

    if (!dateMap[dateKey]) {
      dateMap[dateKey] = { rows: [], total: 0 };
    }

    dateMap[dateKey].rows.push({
      ...row,
      rowTotal,
    });

    dateMap[dateKey].total += rowTotal;
    grandTotal += rowTotal;
  });

  const body = [];

  Object.keys(dateMap)
    .sort(
      (a, b) =>
        moment(a, "DD/MM/YYYY").toDate() -
        moment(b, "DD/MM/YYYY").toDate()
    )
    .forEach(date => {
      dateMap[date].rows.forEach(row => {
        body.push([
          row.counter_name,
          row.username,
          formatAmount(row.cash),
          formatAmount(row.card),
          formatAmount(row.upi),
          formatAmount(row.gift),
          date,
          formatAmount(row.rowTotal),
        ]);
      });

      body.push([
        "",
        "",
        "",
        "",
        "",
        "",
        `${date} Total`,
        formatAmount(dateMap[date].total),
      ]);
    });

  body.push([
    "",
    "",
    "",
    "",
    "",
    "",
    "Grand Total",
    formatAmount(grandTotal),
  ]);

  return {
    headers,
    body,
    filename: `COLLECTION_REPORT_${moment(startDate).format(
      "DD-MM-YYYY"
    )}_TO_${moment(endDate).format("DD-MM-YYYY")}.csv`,
    csvData: [
      [
        "",
        "",
        `Cut-off Collection Report From ${moment(startDate).format(
          "DD-MM-YYYY"
        )}_TO_${moment(endDate).format("DD-MM-YYYY")}`,
        "",
        "",
        "",
        "",
        "",
      ],
      [],
      headers,
      ...body,
    ],
  };
};

//Credit data
const buildCreditExport = tableData => {
  const headers = [
    "Counter Name",
    "User",
    "Customer",
    "Credit Amount",
    "Date",
    "Total",
  ];

  const dateMap = {};
  let grandTotal = 0;

  tableData.forEach(row => {
    const dateKey = moment(row.date).format("DD/MM/YYYY");
    const rowTotal = Number(row.credit || 0);

    if (!dateMap[dateKey]) {
      dateMap[dateKey] = { rows: [], total: 0 };
    }

    dateMap[dateKey].rows.push({
      ...row,
      rowTotal,
    });

    dateMap[dateKey].total += rowTotal;
    grandTotal += rowTotal;
  });

  const body = [];

  Object.keys(dateMap)
    .sort(
      (a, b) =>
        moment(a, "DD/MM/YYYY").toDate() -
        moment(b, "DD/MM/YYYY").toDate()
    )
    .forEach(date => {
      dateMap[date].rows.forEach(row => {
        body.push([
          row.counter_name,
          row.username,
          row.customer,
          formatAmount(row.credit),
          date,
          formatAmount(row.rowTotal),
        ]);
      });

      // 🔹 Date Total Row
      body.push([
        "",
        "",
        "",
        "",
        `${date} Total`,
        formatAmount(dateMap[date].total),
      ]);
    });

  // 🔥 Grand Total
  body.push([
    "",
    "",
    "",
    "",
    "Grand Total",
    formatAmount(grandTotal),
  ]);

  return {
    headers,
    body,
    filename: `CREDIT_REPORT_${moment(startDate).format(
      "DD-MM-YYYY"
    )}_TO_${moment(endDate).format("DD-MM-YYYY")}.csv`,
    csvData: [
      [
        "",
        "",
        `Credit Report From ${moment(startDate).format(
          "DD-MM-YYYY"
        )}_TO_${moment(endDate).format("DD-MM-YYYY")}`,
        "",
        "",
        "",
      ],
      [],
      headers,
      ...body,
    ],
  };
};




  /* ---------------- AUTO CSV DOWNLOAD ---------------- */
  useEffect(() => {
    if (csv.length && isCsv) {
      csvRef.current.link.click();
      setIsCsv(false);
      setCsv([]);
    }
  }, [csv, isCsv]);

  /* ---------------- UI ---------------- */
  return (
    <>
      <CSVLink
        data={csv}
        filename={filename}
        ref={csvRef}
        style={{ display: "none" }}
      />

      <Container fluid>
        <Box p={25}>
          <Flex justify="space-between" align="end">
            <Box>
              <Text size="lg" fw={600}>
                Cut-off Collection Report
              </Text>
              <AppBreadcrumb />
            </Box>

            

            {/* <Button
               color="orange"
               variant={view === "credit" ? "filled" : "outline"}
               onClick={() => {
                 setView("credit");
                 setSearchClicked(false);
               }}
             >
               Credits
             </Button>             

             <Button
               variant={view === "collection" ? "filled" : "outline"}
               onClick={() => {
                 setView("collection");
                 setSearchClicked(false);
               }}
             >
               Collection
             </Button> */}

            <Flex gap={10} align="end">
              <Button
                variant="filled"
                onClick={() => {
                  setView(prev => (prev === "credit" ? "collection" : "credit"));
                  setSearchClicked(false);
                }}
              >
              {view === "collection" ? "Credits" : "Collection"}
            </Button>
              <Button onClick={() => printFilter(true)}>
                <IconDownload size={16} /> CSV
              </Button>
              <Button onClick={() => printFilter(false)}>
                <IconPrinter size={16} /> Print
              </Button>

              <DatePickerInput
                label="Start Date"
                value={startDate}
                onChange={date => {
                  setStartDate(date);
                  setEndDate(null);
                }}
                w={200}
              />

              <DatePickerInput
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                w={200}
                minDate={startDate}
                disabled={!startDate}
              />

              <Button
                color="green"
                onClick={() => {
                  if (!startDate) {
                    showErrorToast({
                      title: "Error",
                      message: "Select Start Date",
                    });
                    return;
                  }
                  setSearchClicked(true);
                }}
              >
                Search
              </Button>
            </Flex>
          </Flex>
        </Box>

        {/* Display Credit Total */}
        <Box px={25}>
          

          <MantineReactTable table={table} />
        </Box>


        {/* <Box px={25}>
          <MantineReactTable table={table} />
        </Box> */}
      </Container>

      <div style={{ display: "none" }}>
        <PrintModal
  title={view === "credit" ? "Credit Report" : "Cut-off Collection Report"}
  subtitle={`Report From ${moment(startDate).format(
    "DD-MM-YYYY"
  )}_TO_${moment(endDate).format("DD-MM-YYYY")}`}
  head={head}
  body={body}
  foot={foot}
  ref={componentRef}
/>

      </div>

      {isMobileScreen && (
        <>
          <FloatingMenu left onClick={() => printFilter(true)}>
            <IconDownload color="white" />
          </FloatingMenu>
          <FloatingMenu right onClick={() => printFilter(false)}>
            <IconPrinter color="white" />
          </FloatingMenu>
        </>
      )}
    </>
  );
}
